import { Controller, UseFormReturn } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import USFormattedNumberInput from '@/components/ui/USFormattedNumberInput';
import { DatePicker } from '@/components/ui/date-picker';
import FormattedPIKInput from '@/components/ui/FormattedPIKInput';
import { LoanFormData } from '@/schemas/loanSchema';

interface RequiredFieldsSectionProps {
  form: UseFormReturn<LoanFormData>;
}

export function RequiredFieldsSection({ form }: RequiredFieldsSectionProps) {
  const {
    control,
    formState: { errors },
  } = form;

  return (
    <div className="p-8 mb-8" data-test-id="required-fields-container">
      {/* Basic Loan Information */}
      <div
        className="grid-2 mb-8"
        data-test-id="basic-loan-information-section"
      >
        <div
          className="form-group loanform-group"
          data-test-id="system-id-field"
        >
          <Label
            className="form-label loanform-label"
            htmlFor="system_id"
            data-test-id="system-id-label"
          >
            System ID *
          </Label>
          <Controller
            name="system_id"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type="number"
                className="form-input loanform-input"
                onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                data-test-id="system-id-input"
              />
            )}
          />
          <p className="form-hint loanform-hint" data-test-id="system-id-help">
            Unique system identifier for the loan (from source file, e.g. 1, 6,
            7, etc.)
          </p>
          {errors.system_id && (
            <p className="form-error" data-test-id="system-id-error">
              {errors.system_id.message}
            </p>
          )}
        </div>

        <div
          className="form-group loanform-group"
          data-test-id="pricing-scenario-field"
        >
          <Label
            className="form-label loanform-label"
            htmlFor="pricing_scenario"
            data-test-id="pricing-scenario-label"
          >
            Pricing Scenario *
          </Label>
          <Controller
            name="pricing_scenario"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  className="form-select loanform-select"
                  data-test-id="pricing-scenario-select"
                >
                  <SelectValue data-test-id="pricing-scenario-value" />
                </SelectTrigger>
                <SelectContent data-test-id="pricing-scenario-content">
                  <SelectItem
                    value="RPX Pricing"
                    data-test-id="pricing-scenario-option-rpx-pricing"
                  >
                    RPX Pricing
                  </SelectItem>
                  <SelectItem
                    value="Custom Scenario"
                    data-test-id="pricing-scenario-option-custom-scenario"
                  >
                    Custom Scenario
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          <p
            className="form-hint loanform-hint"
            data-test-id="pricing-scenario-help"
          >
            Scenario for pricing: 'RPX Pricing' or 'Custom Scenario' as in the
            data file.
          </p>
          {errors.pricing_scenario && (
            <p className="form-error" data-test-id="pricing-scenario-error">
              {errors.pricing_scenario.message}
            </p>
          )}
        </div>
      </div>

      {/* Loan Identification */}
      <div className="grid-2 mb-8" data-test-id="loan-identification-section">
        <div
          className="form-group loanform-group"
          data-test-id="client-loan-number-field"
        >
          <Label
            className="form-label loanform-label"
            htmlFor="loan_identification.client_loan_number"
            data-test-id="client-loan-number-label"
          >
            Client Loan Number *
          </Label>
          <Controller
            name="loan_identification.client_loan_number"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                className="form-input loanform-input"
                data-test-id="client-loan-number-input"
              />
            )}
          />
          {errors.loan_identification?.client_loan_number && (
            <p className="form-error" data-test-id="client-loan-number-error">
              {errors.loan_identification.client_loan_number.message}
            </p>
          )}
        </div>

        <div
          className="form-group loanform-group"
          data-test-id="loan-name-field"
        >
          <Label
            className="form-label loanform-label"
            htmlFor="loan_identification.loan_name"
            data-test-id="loan-name-label"
          >
            Loan Name *
          </Label>
          <Controller
            name="loan_identification.loan_name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                className="form-input loanform-input"
                data-test-id="loan-name-input"
              />
            )}
          />
          {errors.loan_identification?.loan_name && (
            <p className="form-error" data-test-id="loan-name-error">
              {errors.loan_identification.loan_name.message}
            </p>
          )}
        </div>
      </div>

      {/* Property Details */}
      <div className="grid-3 mb-8" data-test-id="property-details-section">
        <div
          className="form-group loanform-group"
          data-test-id="property-sector-field"
        >
          <Label
            className="form-label loanform-label"
            htmlFor="property_details.sector"
            data-test-id="property-sector-label"
          >
            Property Sector *
          </Label>
          <Controller
            name="property_details.sector"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  className="form-select loanform-select"
                  data-test-id="property-sector-select"
                >
                  <SelectValue data-test-id="property-sector-value" />
                </SelectTrigger>
                <SelectContent data-test-id="property-sector-content">
                  <SelectItem
                    value="Multifamily"
                    data-test-id="property-sector-option-multifamily"
                  >
                    Multifamily
                  </SelectItem>
                  <SelectItem
                    value="Multifamily | Affordable Housing"
                    data-test-id="property-sector-option-multifamily-affordable"
                  >
                    Multifamily | Affordable Housing
                  </SelectItem>
                  <SelectItem
                    value="Multifamily | Garden"
                    data-test-id="property-sector-option-multifamily-garden"
                  >
                    Multifamily | Garden
                  </SelectItem>
                  <SelectItem
                    value="Multifamily | Mid-Rise"
                    data-test-id="property-sector-option-multifamily-midrise"
                  >
                    Multifamily | Mid-Rise
                  </SelectItem>
                  <SelectItem
                    value="Multifamily | High-Rise"
                    data-test-id="property-sector-option-multifamily-highrise"
                  >
                    Multifamily | High-Rise
                  </SelectItem>
                  <SelectItem
                    value="Office"
                    data-test-id="property-sector-option-office"
                  >
                    Office
                  </SelectItem>
                  <SelectItem
                    value="Office | CBD"
                    data-test-id="property-sector-option-office-cbd"
                  >
                    Office | CBD
                  </SelectItem>
                  <SelectItem
                    value="Office | Suburban"
                    data-test-id="property-sector-option-office-suburban"
                  >
                    Office | Suburban
                  </SelectItem>
                  <SelectItem
                    value="Retail"
                    data-test-id="property-sector-option-retail"
                  >
                    Retail
                  </SelectItem>
                  <SelectItem
                    value="Retail | Mall"
                    data-test-id="property-sector-option-retail-mall"
                  >
                    Retail | Mall
                  </SelectItem>
                  <SelectItem
                    value="Retail | Grocery Anchored"
                    data-test-id="property-sector-option-retail-grocery"
                  >
                    Retail | Grocery Anchored
                  </SelectItem>
                  <SelectItem
                    value="Industrial"
                    data-test-id="property-sector-option-industrial"
                  >
                    Industrial
                  </SelectItem>
                  <SelectItem
                    value="Industrial | Warehouse"
                    data-test-id="property-sector-option-industrial-warehouse"
                  >
                    Industrial | Warehouse
                  </SelectItem>
                  <SelectItem
                    value="Industrial | Logistics"
                    data-test-id="property-sector-option-industrial-logistics"
                  >
                    Industrial | Logistics
                  </SelectItem>
                  <SelectItem
                    value="Hotel"
                    data-test-id="property-sector-option-hotel"
                  >
                    Hotel
                  </SelectItem>
                  <SelectItem
                    value="Hotel | Full Service"
                    data-test-id="property-sector-option-hotel-full"
                  >
                    Hotel | Full Service
                  </SelectItem>
                  <SelectItem
                    value="Hotel | Limited Service"
                    data-test-id="property-sector-option-hotel-limited"
                  >
                    Hotel | Limited Service
                  </SelectItem>
                  <SelectItem
                    value="Mixed Use | Office/Retail"
                    data-test-id="property-sector-option-mixed-office-retail"
                  >
                    Mixed Use | Office/Retail
                  </SelectItem>
                  <SelectItem
                    value="Other"
                    data-test-id="property-sector-option-other"
                  >
                    Other
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          <p
            className="form-hint loanform-hint"
            data-test-id="property-sector-help"
          >
            Sector of the property (e.g., Multifamily, Office, Retail,
            Industrial, Hotel, Other)
          </p>
          {errors.property_details?.sector && (
            <p className="form-error" data-test-id="property-sector-error">
              {errors.property_details.sector.message}
            </p>
          )}
        </div>

        <div
          className="form-group loanform-group"
          data-test-id="property-type-field"
        >
          <Label
            className="form-label loanform-label"
            htmlFor="property_details.property_type"
            data-test-id="property-type-label"
          >
            Property Type *
          </Label>
          <Controller
            name="property_details.property_type"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                id="property_details.property_type"
                className="form-input loanform-input"
                data-test-id="property-type-input"
              />
            )}
          />
          {errors.property_details?.property_type && (
            <p className="form-error" data-test-id="property-type-error">
              {errors.property_details.property_type.message}
            </p>
          )}
        </div>

        <div
          className="form-group loanform-group"
          data-test-id="lifecycle-financing-field"
        >
          <Label
            className="form-label loanform-label"
            htmlFor="property_details.property_lifecycle_financing"
            data-test-id="lifecycle-financing-label"
          >
            Financing Stage *
          </Label>
          <Controller
            name="property_details.property_lifecycle_financing"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  className="form-select loanform-select"
                  data-test-id="lifecycle-financing-select"
                >
                  <SelectValue data-test-id="lifecycle-financing-value" />
                </SelectTrigger>
                <SelectContent data-test-id="lifecycle-financing-content">
                  <SelectItem
                    value="Permanent"
                    data-test-id="lifecycle-financing-option-permanent"
                  >
                    Permanent
                  </SelectItem>
                  <SelectItem
                    value="Construction"
                    data-test-id="lifecycle-financing-option-construction"
                  >
                    Construction
                  </SelectItem>
                  <SelectItem
                    value="Bridge"
                    data-test-id="lifecycle-financing-option-bridge"
                  >
                    Bridge
                  </SelectItem>
                  <SelectItem
                    value="Refinance"
                    data-test-id="lifecycle-financing-option-refinance"
                  >
                    Refinance
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          <p
            className="form-hint loanform-hint"
            data-test-id="lifecycle-financing-help"
          >
            Type of financing for the property (e.g., Permanent, Construction,
            Bridge, Refinance)
          </p>
          {errors.property_details?.property_lifecycle_financing && (
            <p className="form-error" data-test-id="lifecycle-financing-error">
              {errors.property_details.property_lifecycle_financing.message}
            </p>
          )}
        </div>
      </div>

      {/* Borrower */}
      <div className="mb-8" data-test-id="borrower-section">
        <div
          className="form-group loanform-group"
          data-test-id="sponsor-borrower-field"
        >
          <Label
            className="form-label loanform-label"
            htmlFor="borrower.sponsor_borrower"
            data-test-id="sponsor-borrower-label"
          >
            Sponsor/Borrower
          </Label>
          <Controller
            name="borrower.sponsor_borrower"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                id="borrower.sponsor_borrower"
                className="form-input loanform-input"
                data-test-id="sponsor-borrower-input"
              />
            )}
          />
          {errors.borrower?.sponsor_borrower && (
            <p className="form-error" data-test-id="sponsor-borrower-error">
              {errors.borrower.sponsor_borrower.message}
            </p>
          )}
        </div>
      </div>

      {/* Loan Amounts */}
      <div className="mb-8" data-test-id="loan-amounts-section">
        <div className="grid-3 mb-4" data-test-id="loan-amounts-grid-3">
          <div
            className="form-group loanform-group"
            data-test-id="original-balance-field"
          >
            <Label
              className="form-label loanform-label"
              htmlFor="loan_amounts.original_balance"
              data-test-id="original-balance-label"
            >
              Original Balance/Total Committed Line *
            </Label>
            <Controller
              name="loan_amounts.original_balance"
              control={control}
              render={({ field }) => (
                <USFormattedNumberInput
                  value={field.value}
                  onChange={(val) => field.onChange(val ?? 0)}
                  mode="number"
                  decimals={0}
                  step={1000}
                  className="form-input loanform-input"
                  data-test-id="original-balance-input"
                />
              )}
            />
            {errors.loan_amounts?.original_balance && (
              <p className="form-error" data-test-id="original-balance-error">
                {errors.loan_amounts.original_balance.message}
              </p>
            )}
          </div>

          <div
            className="form-group loanform-group"
            data-test-id="current-balance-field"
          >
            <Label
              className="form-label loanform-label"
              htmlFor="loan_amounts.current_balance"
              data-test-id="current-balance-label"
            >
              Current Balance *
            </Label>
            <Controller
              name="loan_amounts.current_balance"
              control={control}
              render={({ field }) => (
                <USFormattedNumberInput
                  value={field.value}
                  onChange={(val) => field.onChange(val ?? 0)}
                  mode="number"
                  decimals={0}
                  step={1000}
                  className="form-input loanform-input"
                  data-test-id="current-balance-input"
                />
              )}
            />
            {errors.loan_amounts?.current_balance && (
              <p className="form-error" data-test-id="current-balance-error">
                {errors.loan_amounts.current_balance.message}
              </p>
            )}
          </div>

          <div
            className="form-group loanform-group"
            data-test-id="currency-field"
          >
            <Label
              className="form-label loanform-label"
              htmlFor="loan_amounts.currency"
              data-test-id="currency-label"
            >
              Currency *
            </Label>
            <Controller
              name="loan_amounts.currency"
              control={control}
              render={({ field }) => (
                <Select 
                  value={field.value || 'USD'} 
                  onValueChange={field.onChange}
                >
                  <SelectTrigger
                    className="form-select loanform-select"
                    data-test-id="currency-select"
                  >
                    <SelectValue data-test-id="currency-value" placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent data-test-id="currency-content">
                    <SelectItem value="USD" data-test-id="currency-option-usd">
                      USD
                    </SelectItem>
                    <SelectItem value="EUR" data-test-id="currency-option-eur">
                      EUR
                    </SelectItem>
                    <SelectItem value="GBP" data-test-id="currency-option-gbp">
                      GBP
                    </SelectItem>
                    <SelectItem value="CAD" data-test-id="currency-option-cad">
                      CAD
                    </SelectItem>
                    <SelectItem value="AUD" data-test-id="currency-option-aud">
                      AUD
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            <p className="form-hint loanform-hint" data-test-id="currency-help">
              Currency of the loan (e.g., USD, EUR, GBP, CAD, AUD)
            </p>
            {errors.loan_amounts?.currency && (
              <p className="form-error" data-test-id="currency-error">
                {errors.loan_amounts.currency.message}
              </p>
            )}
          </div>
        </div>

        <div className="grid-2" data-test-id="loan-amounts-grid-2">
          <div
            className="form-group loanform-group"
            data-test-id="client-percentage-field"
          >
            <Label
              className="form-label loanform-label"
              htmlFor="loan_amounts.client_percentage"
              data-test-id="client-percentage-label"
            >
              Client % of Total Loan Facility *
            </Label>
            <Controller
              name="loan_amounts.client_percentage"
              control={control}
              render={({ field }) => (
                <USFormattedNumberInput
                  value={field.value}
                  onChange={(val) => field.onChange(val ?? 0)}
                  mode="percent"
                  decimals={1}
                  step={0.1}
                  className="form-input loanform-input"
                  data-test-id="client-percentage-input"
                />
              )}
            />
            <p
              className="form-hint loanform-hint"
              data-test-id="client-percentage-help"
            >
              Client's percentage ownership (0-100)
            </p>
            {errors.loan_amounts?.client_percentage && (
              <p className="form-error" data-test-id="client-percentage-error">
                {errors.loan_amounts.client_percentage.message}
              </p>
            )}
          </div>

          <div
            className="form-group loanform-group"
            data-test-id="pik-balance-field"
          >
            <Label
              className="form-label loanform-label"
              htmlFor="loan_amounts.pik_balance"
              data-test-id="pik-balance-label"
            >
              Payment-in-Kind (PIK) Balance
            </Label>
            <Controller
              name="loan_amounts.pik_balance"
              control={control}
              render={({ field }) => (
                <FormattedPIKInput
                  value={field.value}
                  onChange={(val) => field.onChange(val)}
                  className="form-input loanform-input"
                  data-test-id="pik-balance-input"
                />
              )}
            />
            {errors.loan_amounts?.pik_balance && (
              <p className="form-error" data-test-id="pik-balance-error">
                {errors.loan_amounts.pik_balance.message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Maturity Assumption and Loss Scenario */}
      <div className="grid-2 mb-8" data-test-id="maturity-loss-section">
        <div
          className="form-group loanform-group"
          data-test-id="maturity-assumption-field"
        >
          <Label
            className="form-label loanform-label"
            htmlFor="maturity_assumption"
            data-test-id="maturity-assumption-label"
          >
            Maturity Assumption
          </Label>
          <Controller
            name="maturity_assumption"
            control={control}
            render={({ field }) => (
              <Select value={field.value || ''} onValueChange={field.onChange}>
                <SelectTrigger
                  className="form-select loanform-select"
                  data-test-id="maturity-assumption-select"
                >
                  <SelectValue
                    placeholder="Select maturity assumption"
                    data-test-id="maturity-assumption-value"
                  />
                </SelectTrigger>
                <SelectContent data-test-id="maturity-assumption-content">
                  <SelectItem
                    value="Maturity"
                    data-test-id="maturity-assumption-option-maturity"
                  >
                    Maturity
                  </SelectItem>
                  <SelectItem
                    value="Extension - 1st"
                    data-test-id="maturity-assumption-option-extension-1st"
                  >
                    Extension - 1st
                  </SelectItem>
                  <SelectItem
                    value="Extension - 3rd"
                    data-test-id="maturity-assumption-option-extension-3rd"
                  >
                    Extension - 3rd
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          <p
            className="form-hint loanform-hint"
            data-test-id="maturity-assumption-help"
          >
            If RPX Pricing, RPX Maturity Algo will create the market assumption.
            If Custom Scenario, provide assumption: Maturity, 1st, 2nd, or 3rd
            Extension.
          </p>
          {errors.maturity_assumption && (
            <p className="form-error" data-test-id="maturity-assumption-error">
              {errors.maturity_assumption.message}
            </p>
          )}
        </div>

        <div
          className="form-group loanform-group"
          data-test-id="loss-scenario-field"
        >
          <Label
            className="form-label loanform-label"
            htmlFor="loss_scenario"
            data-test-id="loss-scenario-label"
          >
            Loss Scenario
          </Label>
          <Controller
            name="loss_scenario"
            control={control}
            render={({ field }) => (
              <Select value={field.value || ''} onValueChange={field.onChange}>
                <SelectTrigger
                  className="form-select loanform-select"
                  data-test-id="loss-scenario-select"
                >
                  <SelectValue
                    placeholder="Select scenario"
                    data-test-id="loss-scenario-value"
                  />
                </SelectTrigger>
                <SelectContent data-test-id="loss-scenario-content">
                  <SelectItem value="PD" data-test-id="loss-scenario-option-pd">
                    PD
                  </SelectItem>
                  <SelectItem
                    value="No Loss"
                    data-test-id="loss-scenario-option-no-loss"
                  >
                    No Loss
                  </SelectItem>
                  <SelectItem
                    value="CDR"
                    data-test-id="loss-scenario-option-cdr"
                  >
                    CDR
                  </SelectItem>
                  <SelectItem
                    value="Default Scenario"
                    data-test-id="loss-scenario-option-default"
                  >
                    Default Scenario
                  </SelectItem>
                  <SelectItem
                    value="Custom"
                    data-test-id="loss-scenario-option-custom"
                  >
                    Custom
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          <p
            className="form-hint loanform-hint"
            data-test-id="loss-scenario-help"
          >
            If RPX Pricing, RPX Loss Scenario Algo will create the market
            assumption. If Custom Scenario, provide assumption: Maturity, 1st,
            2nd, or 3rd Extension.
          </p>
          {errors.loss_scenario && (
            <p className="form-error" data-test-id="loss-scenario-error">
              {errors.loss_scenario.message}
            </p>
          )}
        </div>
      </div>

      {/* Credit Spread and Market Yield */}
      <div className="grid-2 mb-8" data-test-id="credit-market-section">
        <div
          className="form-group loanform-group"
          data-test-id="credit-spread-field"
        >
          <Label
            className="form-label loanform-label"
            htmlFor="credit_spread"
            data-test-id="credit-spread-label"
          >
            Credit Spread *
          </Label>
          <Controller
            name="credit_spread"
            control={control}
            render={({ field }) => (
              <USFormattedNumberInput
                value={field.value}
                onChange={(val) => field.onChange(val ?? 0)}
                mode="percent"
                decimals={2}
                step={0.01}
                className="form-input loanform-input"
                data-test-id="credit-spread-input"
              />
            )}
          />
          {errors.credit_spread && (
            <p className="form-error" data-test-id="credit-spread-error">
              {errors.credit_spread.message}
            </p>
          )}
        </div>

        <div
          className="form-group loanform-group"
          data-test-id="market-yield-field"
        >
          <Label
            className="form-label loanform-label"
            htmlFor="market_yield"
            data-test-id="market-yield-label"
          >
            Market Yield (or Discount Rate)
          </Label>
          <Controller
            name="market_yield"
            control={control}
            render={({ field }) => (
              <USFormattedNumberInput
                value={field.value}
                onChange={(val) => field.onChange(val)}
                mode="percent"
                decimals={3}
                step={0.01}
                className="form-input loanform-input"
                data-test-id="market-yield-input"
              />
            )}
          />
          {errors.market_yield && (
            <p className="form-error" data-test-id="market-yield-error">
              {errors.market_yield.message}
            </p>
          )}
        </div>
      </div>

      {/* Risk Metrics - PD, EAD, LGD, Lag to Recovery, Default Date, CDR */}
      <div className="mb-8" data-test-id="risk-metrics-section">
        <h4
          className="text-md font-semibold mb-4"
          data-test-id="risk-metrics-title"
        >
          Risk Metrics
        </h4>
        <div className="grid-3 mb-4" data-test-id="risk-metrics-grid-1">
          <div className="form-group loanform-group" data-test-id="pd-field">
            <Label
              className="form-label loanform-label"
              htmlFor="risk_metrics.pd"
              data-test-id="pd-label"
            >
              PD
            </Label>
            <Controller
              name="risk_metrics.pd"
              control={control}
              render={({ field }) => (
                <USFormattedNumberInput
                  value={field.value}
                  onChange={(val) => field.onChange(val)}
                  mode="percent"
                  decimals={1}
                  step={0.1}
                  className="form-input loanform-input"
                  data-test-id="pd-input"
                />
              )}
            />
            {errors.risk_metrics?.pd && (
              <p className="form-error" data-test-id="pd-error">
                {errors.risk_metrics.pd.message}
              </p>
            )}
          </div>

          <div className="form-group loanform-group" data-test-id="ead-field">
            <Label
              className="form-label loanform-label"
              htmlFor="risk_metrics.ead"
              data-test-id="ead-label"
            >
              EAD
            </Label>
            <Controller
              name="risk_metrics.ead"
              control={control}
              render={({ field }) => (
                <USFormattedNumberInput
                  value={field.value}
                  onChange={(val) => field.onChange(val)}
                  mode="percent"
                  decimals={1}
                  step={0.1}
                  className="form-input loanform-input"
                  data-test-id="ead-input"
                />
              )}
            />
            {errors.risk_metrics?.ead && (
              <p className="form-error" data-test-id="ead-error">
                {errors.risk_metrics.ead.message}
              </p>
            )}
          </div>

          <div className="form-group loanform-group" data-test-id="lgd-field">
            <Label
              className="form-label loanform-label"
              htmlFor="risk_metrics.lgd"
              data-test-id="lgd-label"
            >
              LGD
            </Label>
            <Controller
              name="risk_metrics.lgd"
              control={control}
              render={({ field }) => (
                <USFormattedNumberInput
                  value={field.value}
                  onChange={(val) => field.onChange(val)}
                  mode="percent"
                  decimals={1}
                  step={0.1}
                  className="form-input loanform-input"
                  data-test-id="lgd-input"
                />
              )}
            />
            {errors.risk_metrics?.lgd && (
              <p className="form-error" data-test-id="lgd-error">
                {errors.risk_metrics.lgd.message}
              </p>
            )}
          </div>
        </div>

        <div className="grid-3" data-test-id="risk-metrics-grid-2">
          <div
            className="form-group loanform-group"
            data-test-id="lag-to-recovery-field"
          >
            <Label
              className="form-label loanform-label"
              htmlFor="risk_metrics.lag_to_recovery"
              data-test-id="lag-to-recovery-label"
            >
              Lag to Recovery
            </Label>
            <Controller
              name="risk_metrics.lag_to_recovery"
              control={control}
              render={({ field }) => (
                <USFormattedNumberInput
                  value={field.value}
                  onChange={(val) => field.onChange(val)}
                  mode="integer"
                  decimals={0}
                  step={1}
                  className="form-input loanform-input"
                  data-test-id="lag-to-recovery-input"
                />
              )}
            />
            {errors.risk_metrics?.lag_to_recovery && (
              <p className="form-error" data-test-id="lag-to-recovery-error">
                {errors.risk_metrics.lag_to_recovery.message}
              </p>
            )}
          </div>

          <div
            className="form-group loanform-group"
            data-test-id="default-date-field"
          >
            <Label
              className="form-label loanform-label"
              htmlFor="risk_metrics.default_date"
              data-test-id="default-date-label"
            >
              Default Date
            </Label>
            <Controller
              name="risk_metrics.default_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value ? (typeof field.value === 'string' ? field.value : new Date(field.value).toISOString().split('T')[0]) : ''}
                  onChange={(val) => field.onChange(val ? new Date(val) : undefined)}
                  data-test-id="default-date-input"
                />
              )}
            />
            {errors.risk_metrics?.default_date && (
              <p className="form-error" data-test-id="default-date-error">
                {errors.risk_metrics.default_date.message}
              </p>
            )}
          </div>

          <div className="form-group loanform-group" data-test-id="cdr-field">
            <Label
              className="form-label loanform-label"
              htmlFor="risk_metrics.cdr"
              data-test-id="cdr-label"
            >
              CDR
            </Label>
            <Controller
              name="risk_metrics.cdr"
              control={control}
              render={({ field }) => (
                <USFormattedNumberInput
                  value={field.value}
                  onChange={(val) => field.onChange(val)}
                  mode="percent"
                  decimals={2}
                  step={0.01}
                  className="form-input loanform-input"
                  data-test-id="cdr-input"
                />
              )}
            />
            {errors.risk_metrics?.cdr && (
              <p className="form-error" data-test-id="cdr-error">
                {errors.risk_metrics.cdr.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
